import type { AdapterFetchOptions, AdapterResult, MarketSourceAdapter } from '@/types/adapters';
import type { NormalizedMarketRow } from '@/types/market';
import { appConfig } from '@/config/app';
import { SOURCE_REGISTRY } from '@/data/sourceRegistry';
import { loadSnapshotManifest } from '@/data/snapshotManifest';
import { fetchCsv, parseCsv } from '@/utils/csv';
import { toNum } from '@/utils/numbers';

const DEFAULT_FILE = 'census_acs_county_annual_sample.csv';
const EXPECTED_FIELDS = 3;

/**
 * Census ACS adapter: slow-moving annual housing-stock benchmarks (home
 * value estimate, rent, homeownership rate). Used as a comparison/context
 * layer, not a sale-flow source -- this is why it does not populate
 * medianSalePrice/DOM/inventory fields.
 */
export const censusAcsAdapter: MarketSourceAdapter = {
  meta: SOURCE_REGISTRY['census-acs'],

  async fetch(options?: AdapterFetchOptions): Promise<AdapterResult> {
    const warnings: string[] = [];
    const manifest = await loadSnapshotManifest(options?.signal);
    const entry = manifest['census-acs'];
    const file = entry?.file ?? DEFAULT_FILE;
    const isSampleData = entry?.isSample ?? true;

    let text: string;
    try {
      text = await fetchCsv(`${appConfig.dataSnapshotBasePath}/${file}`, options?.signal);
    } catch (err) {
      warnings.push(`census-acs: could not load ${file}: ${(err as Error).message}`);
      return { rows: [], warnings, isSampleData };
    }

    const records = parseCsv(text);
    const rows: NormalizedMarketRow[] = [];

    for (const r of records) {
      const geoId = r.county_fips?.trim();
      if (!geoId) continue;

      const medianHomeValueEstimate = toNum(r.median_home_value);
      const medianGrossRent = toNum(r.median_gross_rent);
      const homeownershipRatePct = toNum(r.homeownership_rate_pct);

      const filledCount = [medianHomeValueEstimate, medianGrossRent, homeownershipRatePct].filter(
        (v) => v !== undefined,
      ).length;

      rows.push({
        geoLevel: 'county',
        geoId,
        geoName: r.county_name?.trim() || geoId,
        sourceId: 'census-acs',
        sourceLabel: entry?.label ?? SOURCE_REGISTRY['census-acs'].label,
        periodLabel: r.year?.trim() || entry?.periodLabel || 'unknown',
        publishedAt: r.published_at?.trim() || entry?.publishedAt || new Date().toISOString().slice(0, 10),
        retrievedAt: new Date().toISOString(),
        medianHomeValueEstimate,
        medianGrossRent,
        homeownershipRatePct,
        fieldCompleteness: filledCount / EXPECTED_FIELDS,
      });
    }

    return { rows, warnings, isSampleData };
  },
};
